import React, { useState, useEffect, useMemo } from 'react';
import { Col, FormGroup, Label, Input, Button, Tooltip } from 'reactstrap';
import Select from 'react-select';
import { ComponentCard } from '../layout/ComponentCard';
import { Instancia } from '../helpers/axios';
import usePasarelaActiva from '../../../hooks/usePasarelaActiva';
import { PASARELAS, labelPasarela } from '../../../helpers/pasarelas';

export const PagoMatricula = ({
  selectedOption,
  beginning,
  next,
  previous,
  empresaId,
  deployment,
  local,
  setPaymentMethod,
  setPaymentType,
  paymentMethod,
  paymentType,
  isFirstPeriod,
  isSede,
  nombreGrupo,
  id,
  precioFinal,
  mode,
  cuotaDates,
  setCuotaDates,
}) => {
  const leadParam = mode === 'presupuesto' ? '&isLead=true' : '';
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [isNewStudent, setIsNewStudent] = useState(false); // Estado para el nuevo alumno
  const userSession = JSON.parse(sessionStorage.getItem('user'));
  const { pasarela } = usePasarelaActiva(empresaId);
  const pasarelaOnline = pasarela || PASARELAS.STRIPE;

  const isPresencial = !nombreGrupo.includes('NP');

  const peticionesApi = async () => {
    // Declaramos instancia de axios
    const instance = Instancia(deployment);

    // peticiones a la API
    try {
      // Petición para obtener grupos
      const grupos = await instance.get(
        `nuevoEstudiante?headquarter=${empresaId}&userEmail=${userSession?.profileObj.email}&id=${local.id}&name=${nombreGrupo}${leadParam}`,
      );

      setIsNewStudent(grupos.data.estado);
    } catch (error) {
      // Si se produce un error, entramos en este bloque
      sessionStorage.setItem('apiError', error);
      sessionStorage.removeItem('user');
    }
  };

  useEffect(() => {
    if (empresaId && nombreGrupo && id) {
      peticionesApi();
    }
  }, [empresaId, nombreGrupo, id]);

  // Para presupuestos: 1..12 cuotas y todos los métodos de pago, sin restricciones
  const PRESUPUESTO_PAYMENT_TYPES = [
    'pagoUnico',
    'pagoDosCuotas',
    'pagoTresCuotas',
    'pagoCuatroCuotas',
    'pagoCincoCuotas',
    'pagoSeisCuotas',
    'pagoSieteCuotas',
    'pagoOchoCuotas',
    'pagoNueveCuotas',
    'pagoDiezCuotas',
    'pagoOnceCuotas',
    'pagoDoceCuotas',
  ];
  const PRESUPUESTO_PAYMENT_METHODS = [
    pasarelaOnline,
    'transferencia',
    'efectivo',
    'tarjetaCredito',
  ];

  // Opciones de tipo de pago según las condiciones
  const paymentTypeOptions = useMemo(() => {
    if (mode === 'presupuesto') return PRESUPUESTO_PAYMENT_TYPES;

    // Si no es de sede, solo "sin recibo"
    if (!isSede) {
      return ['sinRecibo'];
    }

    // Matrícula completa: pago único, 2 cuotas o 7 cuotas
    if (selectedOption?.toString() === '1') {
      return ['pagoUnico', 'pagoDosCuotas', 'pagoSieteCuotas', 'sinRecibo'];
    }

    // Matrícula personalizada: pago único, 2 cuotas o 4 cuotas
    if (selectedOption?.toString() !== '1') {
      return ['pagoUnico', 'pagoDosCuotas', 'pagoCuatroCuotas', 'sinRecibo'];
    }

    return [];
  }, [
    mode,
    precioFinal, // <- se recalcula si cambia
    selectedOption,
    isSede,
    isFirstPeriod,
    isPresencial,
    isNewStudent,
    nombreGrupo,
  ]);

  // Opciones de método de pago según el tipo seleccionado
  const paymentMethodOptions = (() => {
    if (mode === 'presupuesto') return PRESUPUESTO_PAYMENT_METHODS;

    switch (paymentType) {
      case 'pagoDosCuotas':
      case 'pagoCuatroCuotas':
      case 'pagoSieteCuotas':
        // 2 o más cuotas: pasarela online, transferencia y tarjeta (sin efectivo)
        return [pasarelaOnline, 'transferencia', 'tarjetaCredito'];
      case 'pagoUnico':
        // 1 cuota: efectivo, pasarela online, transferencia y tarjeta (solo si es de sede)
        return isSede ? ['efectivo', pasarelaOnline, 'transferencia', 'tarjetaCredito'] : [];
      case 'sinRecibo':
        // Sin recibo: todas las opciones si es de sede, solo transferencia si no es de sede
        return isSede ? [pasarelaOnline, 'efectivo', 'tarjetaCredito'] : ['transferencia'];
      default:
        return [];
    }
  })();

  const isPresupuesto = mode === 'presupuesto';

  // Selección automática si solo hay una opción de tipo de pago (no aplica en presupuestos)
  useEffect(() => {
    if (isPresupuesto) return;
    if (paymentTypeOptions.length === 1 && !paymentType) {
      setPaymentType(paymentTypeOptions[0]);
    }
  }, [paymentTypeOptions, paymentType, setPaymentType, isPresupuesto]);

  // Selección automática si solo hay una opción de método de pago (no aplica en presupuestos)
  useEffect(() => {
    if (isPresupuesto) return;
    if (paymentMethodOptions.length === 1 && !paymentMethod) {
      setPaymentMethod(paymentMethodOptions[0]);
    }
  }, [paymentMethodOptions, paymentMethod, setPaymentMethod, isPresupuesto]);

  const isNextDisabled = isPresupuesto
    ? !Array.isArray(paymentType) || paymentType.length === 0
    : !paymentType || !paymentMethod;

  // Mostrar tooltip solo si el botón existe
  useEffect(() => {
    setTooltipOpen(isNextDisabled);
  }, [isNextDisabled]);

  const handlePaymentChange = (e) => {
    setPaymentType(e.target.value);
    setPaymentMethod(''); // Reiniciar método de pago al cambiar tipo de pago
  };

  const handleMethodChange = (e) => {
    setPaymentMethod(e.target.value);
  };

  const labelByKey = {
    pagoUnico: isPresupuesto ? '1 cuota' : 'Pago Único',
    pagoDosCuotas: '2 cuotas',
    pagoTresCuotas: '3 cuotas',
    pagoCuatroCuotas: '4 cuotas',
    pagoCincoCuotas: '5 cuotas',
    pagoSeisCuotas: '6 cuotas',
    pagoSieteCuotas: '7 cuotas',
    pagoOchoCuotas: '8 cuotas',
    pagoNueveCuotas: '9 cuotas',
    pagoDiezCuotas: '10 cuotas',
    pagoOnceCuotas: '11 cuotas',
    pagoDoceCuotas: '12 cuotas',
    sinRecibo: 'Sin recibo',
  };

  const cuotaOptions = paymentTypeOptions.map((option) => ({
    value: option,
    label: labelByKey[option] || option,
  }));

  const selectedCuotas = Array.isArray(paymentType)
    ? cuotaOptions.filter((opt) => paymentType.includes(opt.value))
    : [];

  const handleCuotasMultiChange = (selected) => {
    setPaymentType((selected || []).map((opt) => opt.value));
  };

  // Mapa key → nº de cuotas (para el desglose informativo en presupuesto).
  const cuotasCountMap = {
    pagoUnico: 1,
    pagoDosCuotas: 2,
    pagoTresCuotas: 3,
    pagoCuatroCuotas: 4,
    pagoCincoCuotas: 5,
    pagoSeisCuotas: 6,
    pagoSieteCuotas: 7,
    pagoOchoCuotas: 8,
    pagoNueveCuotas: 9,
    pagoDiezCuotas: 10,
    pagoOnceCuotas: 11,
    pagoDoceCuotas: 12,
  };

  const desglosePorCuota = isPresupuesto
    ? selectedCuotas
        .map((opt) => ({ key: opt.value, n: cuotasCountMap[opt.value] || 0 }))
        .filter((it) => it.n > 0)
        .sort((a, b) => a.n - b.n)
        .map(({ key, n }) => {
          const total = typeof precioFinal === 'number' ? precioFinal : 0;
          const porCuota = total / n;
          return {
            key,
            n,
            label: labelByKey[key] || key,
            porCuota,
            total,
          };
        })
    : [];

  const todayISO = new Date().toISOString().split('T')[0];

  const getCuotaDate = (key, field) => (cuotaDates?.[key]?.[field] || '');

  const handleCuotaInicioChange = (key, value) => {
    if (!setCuotaDates) return;
    setCuotaDates((prev) => {
      const current = prev?.[key] || { inicio: '', fin: '', tipo: '' };
      const next = { ...current, inicio: value };
      if (next.tipo !== '1' && next.fin && value && next.fin < value) {
        next.fin = '';
      }
      return { ...(prev || {}), [key]: next };
    });
  };

  const handleCuotaFinChange = (key, value) => {
    if (!setCuotaDates) return;
    setCuotaDates((prev) => {
      const current = prev?.[key] || { inicio: '', fin: '', tipo: '' };
      return { ...(prev || {}), [key]: { ...current, fin: value } };
    });
  };

  const handleCuotaTipoChange = (key, value) => {
    if (!setCuotaDates) return;
    setCuotaDates((prev) => {
      const current = prev?.[key] || { inicio: '', fin: '', tipo: '' };
      const next = { ...current, tipo: value };
      if (value === '1') next.fin = '';
      return { ...(prev || {}), [key]: next };
    });
  };

  const TIPO_OPTIONS = [
    { value: '1', label: 'U' },
    { value: '2', label: 'Y' },
    { value: '3', label: 'A' },
  ];

  const fullName = `${local.nombre} ${local.apellidos}`;

  return (
    <Col md="12">
      <ComponentCard
        hasButton={false}
        className="clearfix"
        name={fullName}
        title={
          selectedOption === '1'
            ? `Matrícula completa - ${empresaId}`
            : `Matrícula personalizada - ${empresaId}`
        }
      >
        {typeof precioFinal === 'number' ? (
          <>
            {' '}
            {/* Tipo de Pago */}
            <FormGroup>
              <Label for="paymentType">{isPresupuesto ? 'Cuotas' : 'Tipo de Pago'}</Label>
              {isPresupuesto ? (
                <Select
                  inputId="paymentType"
                  isMulti
                  options={cuotaOptions}
                  value={selectedCuotas}
                  onChange={handleCuotasMultiChange}
                  placeholder="Seleccione una o varias cuotas"
                  closeMenuOnSelect={false}
                />
              ) : (
                <Input
                  type="select"
                  name="paymentType"
                  id="paymentType"
                  value={paymentType}
                  onChange={handlePaymentChange}
                >
                  <option value="">Seleccione una opción</option>
                  {paymentTypeOptions.map((option) => (
                    <option key={option} value={option}>
                      {labelByKey[option] || option}
                    </option>
                  ))}
                </Input>
              )}
            </FormGroup>
            {isPresupuesto && desglosePorCuota.length > 0 && (
              <div
                style={{
                  marginTop: '8px',
                  marginBottom: '16px',
                  padding: '12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  backgroundColor: '#f9fafb',
                  fontSize: '0.85rem',
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: '8px', color: '#374151' }}>
                  Desglose por cuota{' '}
                  <span style={{ color: '#9ca3af', fontWeight: 400 }}>
                    (Total: {(typeof precioFinal === 'number' ? precioFinal : 0).toFixed(2)} €)
                  </span>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #e5e7eb', color: '#6b7280' }}>
                      <th style={{ textAlign: 'left', padding: '6px 4px', fontWeight: 600 }}>
                        Opción
                      </th>
                      <th style={{ textAlign: 'right', padding: '6px 4px', fontWeight: 600 }}>
                        Importe
                      </th>
                      <th style={{ textAlign: 'left', padding: '6px 4px', fontWeight: 600 }}>
                        Fecha inicio
                      </th>
                      <th style={{ textAlign: 'left', padding: '6px 4px', fontWeight: 600 }}>
                        Tipo
                      </th>
                      <th style={{ textAlign: 'left', padding: '6px 4px', fontWeight: 600 }}>
                        Fecha fin
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {desglosePorCuota.map(({ key, n, label, porCuota }) => {
                      const inicio = getCuotaDate(key, 'inicio');
                      const tipo = getCuotaDate(key, 'tipo');
                      const fin = tipo === '1' ? '' : getCuotaDate(key, 'fin');
                      const finDisabled = tipo === '1';
                      return (
                        <tr key={key} style={{ borderTop: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '6px 4px', color: '#374151' }}>{label}</td>
                          <td
                            style={{
                              padding: '6px 4px',
                              textAlign: 'right',
                              color: '#059669',
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {porCuota.toFixed(2)} €{n > 1 ? ' / cuota' : ''}
                          </td>
                          <td style={{ padding: '6px 4px' }}>
                            <Input
                              type="date"
                              bsSize="sm"
                              min={todayISO}
                              value={inicio}
                              onChange={(e) => handleCuotaInicioChange(key, e.target.value)}
                              style={{ fontSize: '0.8rem' }}
                            />
                          </td>
                          <td style={{ padding: '6px 4px' }}>
                            <Input
                              type="select"
                              bsSize="sm"
                              value={getCuotaDate(key, 'tipo')}
                              onChange={(e) => handleCuotaTipoChange(key, e.target.value)}
                              style={{ fontSize: '0.8rem' }}
                            >
                              <option value="">-</option>
                              {TIPO_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </Input>
                          </td>
                          <td style={{ padding: '6px 4px' }}>
                            <Input
                              type="date"
                              bsSize="sm"
                              min={inicio || todayISO}
                              value={fin}
                              disabled={finDisabled}
                              onChange={(e) => handleCuotaFinChange(key, e.target.value)}
                              style={{ fontSize: '0.8rem' }}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {/* Método de Pago (oculto en presupuestos) */}
            {!isPresupuesto && (
              <FormGroup>
                <Label for="paymentMethod">Método de Pago</Label>
                <Input
                  type="select"
                  name="paymentMethod"
                  id="paymentMethod"
                  value={paymentMethod}
                  onChange={handleMethodChange}
                >
                  <option value="">Seleccione una opción</option>
                  {paymentMethodOptions.map((option) => (
                    <option key={option} value={option}>
                      {option === PASARELAS.STRIPE || option === PASARELAS.REDSYS
                        ? labelPasarela(option)
                        : option === 'tarjetaCredito'
                        ? 'Tarjeta de crédito'
                        : option === 'efectivo'
                        ? 'Efectivo'
                        : 'Transferencia'}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            )}
          </>
        ) : (
          <p>Cargando opciones de pago...</p>
        )}
        {/* Botones */}
        <div className="pt-2" style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button onClick={previous}>Atrás</Button>
          <Button onClick={beginning}>
            {selectedOption === 'portal' ? 'Volver a detalles' : 'Volver a la ficha de alumno'}
          </Button>
          <Button
            type="button"
            id="nextBtn" // <- este id se usará como target del tooltip
            onClick={() => next(0)}
            disabled={isNextDisabled}
          >
            Siguiente
          </Button>

          <Tooltip
            placement="top"
            isOpen={tooltipOpen}
            target="nextBtn" // <- usar el id aquí, no la referencia
          >
            Falta información
          </Tooltip>
        </div>
      </ComponentCard>
    </Col>
  );
};
